import type { ReactNode } from "react"
import { useCallback, useContext } from "react"
import { IconSquareArrowUpRightOutline18 as SquareArrowOutUpRight } from "nucleo-ui-outline-18"

import {
	cn,
	getPortalDisplayUrl,
	getPortalUrl,
} from "@productlane/lib/src/browser"
import { Input, Loader, Switch } from "@productlane/ui"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { useZeroWorkspace } from "@/lib/zero"
import { useActivePortalInstanceStore } from "@/stores/active-portal-instance-store"
import { SettingsCardInsetContext } from "./settings-switches/settings-container"

interface Props {
	title: string
	link: string
	description: string
	onCheckedChange?: (value: boolean) => Promise<void>
	checked: boolean
	disabled?: boolean
	// Replaces the switch, for plans that cannot publish yet.
	action?: ReactNode
}

export const PublishPortalCard = ({
	title,
	description,
	onCheckedChange,
	checked,
	link,
	disabled = false,
	action,
}: Props) => {
	const workspace = useZeroWorkspace()
	const inset = useContext(SettingsCardInsetContext)
	const titleSmallCase = title.toLocaleLowerCase()
	const activePortalInstanceId = useActivePortalInstanceStore(
		(s) => s.activePortalInstanceId,
	)
	const portalSlug =
		(workspace?.portalInstances ?? []).find(
			(instance) => instance.id === activePortalInstanceId,
		)?.slug ?? null

	const copyPublicLinkToClipboard = useCallback(async () => {
		if (!workspace) {
			return
		}
		await navigator.clipboard.writeText(
			getPortalDisplayUrl(workspace, `/${link}`, portalSlug),
		)
		toast.success({ title: `Public ${titleSmallCase} link copied!` })
		analytics.track(`copy-${titleSmallCase}-link`)
	}, [workspace, titleSmallCase, link, portalSlug])

	if (!workspace) {
		return (
			<div className="flex items-center justify-center">
				<Loader />
			</div>
		)
	}

	const getTitle = () => {
		const isOrAre = titleSmallCase.endsWith("s") ? "are" : "is"

		return checked ? `${title} ${isOrAre} public` : `Make ${title} public`
	}

	return (
		<div
			className={cn(
				"rounded-[10px] bg-white p-4 shadow-card dark:bg-gray-850 dark:shadow-card-dark",
				inset && "-mx-3.5",
			)}
		>
			<div className="flex w-full flex-col items-start gap-2 text-sm">
				<div
					data-testid="publish-portal-card-label"
					className="flex w-full select-none items-center justify-between"
				>
					<div className="flex w-full flex-col items-start gap-0.5">
						<div className="flex w-full flex-row items-center justify-between gap-4">
							<div className="text-sm font-medium">{getTitle()}</div>
							{action ?? (
								<Switch
									label={null}
									name="public"
									checked={checked}
									onCheckedChange={onCheckedChange}
									disabled={disabled}
								/>
							)}
						</div>
						<div className="text-xs font-medium text-secondary">
							{description}
						</div>
					</div>
				</div>

				<Input
					readOnly
					value={getPortalDisplayUrl(workspace, `/${link}`, portalSlug)}
					onClick={() => copyPublicLinkToClipboard()}
					name="domain"
					className="mt-3 w-full flex-1"
					inputClassName="hover:text-secondary"
					icon={SquareArrowOutUpRight}
					iconPosition="right"
					iconOnClick={() =>
						window.open(
							getPortalUrl(workspace, `/${link}`, portalSlug),
							"_blank",
						)
					}
				/>
			</div>
		</div>
	)
}
